/**
 * How a workflow value is parsed, compared and printed.
 *
 * These four live together because the save path and the run path have to agree
 * about every one of them. A date the editor accepts and the evaluator rejects,
 * or an amount rounded one way when saved and another way when compared, is a
 * rule that behaves differently from the sentence its author read back. Two
 * copies of that logic is the one arrangement that cannot guarantee it, so
 * there is exactly one.
 */

export type WorkflowAmountRange = {
  readonly min: number
  readonly max: number
}

type WorkflowAmountRangeCandidate =
  | import("../primitives.js").JsonValue
  | {
      readonly min?: import("../primitives.js").JsonValue
      readonly max?: import("../primitives.js").JsonValue
    }

const WorkflowAmountRangeValueSchema = z.object({
  min: z.number(),
  max: z.number(),
})

export function isWorkflowAmountRange(
  value: WorkflowAmountRangeCandidate | undefined
): value is WorkflowAmountRange {
  return WorkflowAmountRangeValueSchema.safeParse(value).success
}

export const isoDayPattern = /^\d{4}-\d{2}-\d{2}$/

/**
 * A calendar day as a UTC timestamp, or null when the string is not a real one.
 *
 * Strict on both counts, and this is the strictness the saved value is
 * validated with as well as the one the comparison uses.
 * `Date.parse("2026-02-30")` does not fail: it rolls forward to the second of
 * March. A lenient parse at either end would let a rule scoped to February fire
 * on a March date, or compare against a day the document does not carry.
 */
export function toUtcDay(value: string): number | null {
  if (!isoDayPattern.test(value)) {
    return null
  }

  const parsed = new Date(`${value}T00:00:00.000Z`)

  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed.toISOString().slice(0, 10) === value ? parsed.getTime() : null
}

/** The start of the day `now` falls in, in UTC. */
export function startOfUtcDay(now: Date): number {
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
}

/**
 * Amounts compare in whole cents, so an amount on the boundary is exact and a
 * repeating binary fraction cannot drift. Rounded rather than truncated:
 * 0.29 * 100 is 28.999999999999996, and truncating it would read a 29 cent
 * document as 28 cents.
 */
export function toCents(value: number): number {
  return Math.round(value * 100)
}

/** An amount written out, for a sentence or a history row. */
export function formatAmount(value: number): string {
  return value.toFixed(2)
}

/**
 * A label from a lookup map, falling back to the raw key. Written once because
 * every one of these maps can fall behind the vocabulary it labels, and a
 * missing label must never throw inside the evaluator: a run that cannot be
 * described still has to be recorded.
 */
export function labelOr(
  labels: Readonly<Record<string, string>>,
  key: string
): string {
  return labels[key] ?? key
}
import { z } from "zod"
